import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircleIcon from "@mui/icons-material/Circle";
import ThreadList from "./ThreadList";
import ConversationView from "./ConversationView";
import SettingsPanel from "./SettingsPanel";
import "./campaignInbox.css";

const POLL_INTERVAL_MS = 30000;

const AUTH_NOTICES = {
  success: { tone: "ok", text: "Gmail connected successfully." },
  error: { tone: "error", text: "Something went wrong connecting Gmail — please try again." },
  invalid_state: { tone: "error", text: "That connection link expired — please click Connect Gmail again." },
  no_refresh_token: { tone: "error", text: "Google didn't grant offline access — disconnect any prior authorization for this app in your Google account and try again." },
};

export default function CampaignInbox() {
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [connection, setConnection] = useState({ status: "disconnected", connectedEmail: null, syncLabelIds: [], signatureHtml: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [authNotice, setAuthNotice] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [labels, setLabels] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [companySignatureHtml, setCompanySignatureHtml] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authResult = params.get("campaignInboxAuth");
    if (authResult) {
      setAuthNotice(AUTH_NOTICES[authResult] || null);
      // Right after a fresh connect, open Settings straight to the label
      // picker — otherwise sync silently defaults to "all mail" until
      // someone remembers to go restrict it manually.
      if (authResult === "success") setSettingsOpen(true);
      params.delete("campaignInboxAuth");
      const newSearch = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (newSearch ? `?${newSearch}` : ""));
    }
  }, []);

  const fetchThreads = useCallback(() => {
    axios
      .get("/api/campaign-inbox/threads", {
        params: {
          filter: filter === "all" ? undefined : filter,
          search: search || undefined,
        },
      })
      .then((res) => {
        if (res.data.success) setThreads(res.data.data);
      })
      .catch(() => {});
  }, [filter, search]);

  const fetchSettings = useCallback(() => {
    axios
      .get("/api/campaign-inbox/settings")
      .then((res) => {
        if (res.data.success) setConnection(res.data.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchThreads();
    const id = setInterval(fetchThreads, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchThreads]);

  useEffect(() => {
    fetchSettings();
    const id = setInterval(fetchSettings, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSettings]);

  const fetchTemplates = useCallback(() => {
    axios
      .get("/api/campaign-inbox/templates")
      .then((res) => {
        if (res.data.success) setTemplates(res.data.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    axios
      .get("/api/campaign-inbox/company-signature")
      .then((res) => {
        if (res.data.success) setCompanySignatureHtml(res.data.data.signatureHtml || "");
      })
      .catch(() => {});
  }, []);

  // Only fetched once the Settings panel is opened and Gmail is connected —
  // there's no reason to hit the Gmail API for a picker nobody's looking at.
  useEffect(() => {
    if (!settingsOpen || connection.status !== "connected") return;
    setLabelsLoading(true);
    axios
      .get("/api/campaign-inbox/labels")
      .then((res) => {
        if (res.data.success) setLabels(res.data.data);
      })
      .catch(() => {})
      .finally(() => setLabelsLoading(false));
  }, [settingsOpen, connection.status]);

  // `silent` skips the loading-spinner state — used for background polling
  // refreshes so an already-open conversation doesn't flash to a "Loading…"
  // placeholder and back every 30s.
  const fetchThreadDetail = useCallback((threadId, { markRead, silent } = {}) => {
    if (!silent) setThreadLoading(true);
    axios
      .get(`/api/campaign-inbox/threads/${threadId}`)
      .then((res) => {
        if (!res.data.success) return;
        setSelectedThread(res.data.data.thread);
        setMessages(res.data.data.messages);
        if (markRead && res.data.data.thread.unreadCount > 0) {
          axios.patch(`/api/campaign-inbox/threads/${threadId}/read`).catch(() => {});
          setThreads((prev) => prev.map((t) => (t._id === threadId ? { ...t, unreadCount: 0 } : t)));
        }
      })
      .finally(() => {
        if (!silent) setThreadLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    fetchThreadDetail(selectedId, { markRead: true });
    const id = setInterval(() => fetchThreadDetail(selectedId, { silent: true }), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [selectedId, fetchThreadDetail]);

  const handleSelect = (threadId) => {
    setSelectedId(threadId);
  };

  const handleSend = (threadId, bodyHtml, { cc, bcc, includeSignature } = {}) => {
    setSending(true);
    axios
      .post(`/api/campaign-inbox/threads/${threadId}/reply`, { bodyHtml, cc, bcc, includeSignature })
      .then((res) => {
        if (res.data.success) {
          setMessages((prev) => [...prev, res.data.data]);
          fetchThreads();
        }
      })
      .catch((err) => {
        window.alert(err.response?.data?.error || "Failed to send reply.");
      })
      .finally(() => setSending(false));
  };

  const handleRelink = (threadId, crmId) => {
    axios
      .post(`/api/campaign-inbox/threads/${threadId}/relink`, { crmId })
      .then(() => {
        fetchThreadDetail(threadId);
        fetchThreads();
      })
      .catch(() => {});
  };

  const handleRecheckLabel = (threadId) => {
    axios
      .post(`/api/campaign-inbox/threads/${threadId}/relink`, { recheckLabel: true })
      .then(() => fetchThreadDetail(threadId))
      .catch(() => {});
  };

  const handleRefresh = () => {
    setRefreshing(true);
    axios
      .post("/api/campaign-inbox/sync")
      .then((res) => {
        if (res.data.success) {
          fetchThreads();
          fetchSettings();
          if (selectedId) fetchThreadDetail(selectedId, { silent: true });
        }
      })
      .catch((err) => {
        window.alert(err.response?.data?.error || "Failed to refresh mail.");
      })
      .finally(() => setRefreshing(false));
  };

  const handleConnect = () => {
    return axios.get("/api/campaign-inbox/connect").then((res) => {
      if (res.data.success && res.data.url) {
        window.location.href = res.data.url;
      }
    });
  };

  const handleDisconnect = () => {
    axios.post("/api/campaign-inbox/disconnect").then(fetchSettings).catch(() => {});
  };

  const handleSyncLabelsChange = (syncLabelIds) => {
    setConnection((prev) => ({ ...prev, syncLabelIds }));
    axios.patch("/api/campaign-inbox/settings", { syncLabelIds }).catch(() => fetchSettings());
  };

  const handleSignatureChange = (signatureHtml) => {
    setConnection((prev) => ({ ...prev, signatureHtml }));
    axios.patch("/api/campaign-inbox/settings", { signatureHtml }).catch(() => fetchSettings());
  };

  const handleSaveTemplate = (name, bodyHtml) => {
    axios.post("/api/campaign-inbox/templates", { name, bodyHtml }).then(fetchTemplates).catch(() => {});
  };

  const handleDeleteTemplate = (templateId) => {
    axios.delete(`/api/campaign-inbox/templates/${templateId}`).then(fetchTemplates).catch(() => {});
  };

  return (
    <div className="cinbox-root">
      {authNotice && (
        <div className={`cinbox-auth-notice cinbox-auth-notice--${authNotice.tone}`}>
          <span>{authNotice.text}</span>
          <button className="cinbox-icon-btn" onClick={() => setAuthNotice(null)}>×</button>
        </div>
      )}

      <div className="cinbox-shell">
        <div className="cinbox-shell-header">
          <div className="cinbox-topbar-status">
            <CircleIcon className={connection.status === "connected" ? "cinbox-dot-on" : "cinbox-dot-off"} />
            <span>
              {connection.status === "connected"
                ? `Connected — ${connection.connectedEmail}`
                : connection.status === "error"
                ? "Gmail connection error — reconnect required"
                : "Gmail not connected"}
            </span>
            <span className="cinbox-topbar-sep">•</span>
            <span>
              Sync labels: <strong>{connection.syncLabelIds?.length ? `${connection.syncLabelIds.length} selected` : "All mail"}</strong>
            </span>
          </div>
          <div className="cinbox-topbar-actions">
            <button
              className="stripe-btn-clear"
              onClick={handleRefresh}
              disabled={refreshing || connection.status !== "connected"}
              title={connection.status !== "connected" ? "Connect Gmail to refresh" : "Fetch new mail now"}
            >
              <RefreshIcon fontSize="small" className={refreshing ? "cinbox-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button className="stripe-btn-clear" onClick={() => setSettingsOpen(true)}>
              <SettingsIcon fontSize="small" /> Settings
            </button>
          </div>
        </div>

        <div className="cinbox-body">
          <ThreadList
            threads={threads}
            selectedId={selectedId}
            onSelect={handleSelect}
            filter={filter}
            onFilterChange={setFilter}
            search={search}
            onSearchChange={setSearch}
          />
          <ConversationView
            thread={selectedThread}
            messages={messages}
            loading={threadLoading}
            sending={sending}
            onSend={handleSend}
            onRelink={handleRelink}
            onRecheckLabel={handleRecheckLabel}
            templates={templates}
          />
        </div>
      </div>

      {settingsOpen && (
        <SettingsPanel
          connection={connection}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          onSignatureChange={handleSignatureChange}
          labels={labels}
          labelsLoading={labelsLoading}
          onSyncLabelsChange={handleSyncLabelsChange}
          templates={templates}
          onSaveTemplate={handleSaveTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          companySignatureHtml={companySignatureHtml}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}
